import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { SIDE_MENU, type MenuItem } from "./menu";
import { useAuth } from "../../../store/useAuthStore";
import { cn } from "../../../utils/cn.utils";
import type { Role } from "../../../types/auth.types";
import { useQuery } from "@tanstack/react-query";
import { getAnalytics } from "../../../lib/apis/v1/analytics.service";
import { formatDateTime } from "../../../utils/date.utils";
import dayjs from "dayjs";

export default function Aside() {
  const location = useLocation();
  const auth = useAuth();
  const [openSet, setOpenSet] = useState<Set<string>>(new Set());

  // 1. Aside에서 사용하던 권한 필터링 로직 그대로 이식
  const { mainMenuItems } = useMemo(() => {
    const filterByAuth = (items: MenuItem[]): MenuItem[] => {
      return items.reduce((acc, item) => {
        // console.log(item.auth, auth.role)
        const hasAuth = !item.auth || (item.auth as Role[]).includes(auth?.role as Role);

        if (!hasAuth) return acc;

        if (item.children && item.children.length > 0) {
          const visibleChildren = filterByAuth(item.children);
          if (visibleChildren.length > 0 || item.to) {
            acc.push({ ...item, children: visibleChildren });
          }
        } else {
          acc.push(item);
        }
        return acc;
      }, [] as MenuItem[]);
    };

    const filtered = filterByAuth(SIDE_MENU as unknown as MenuItem[]);

    return { mainMenuItems: filtered };
  }, [auth.role]);

  // 2. 경로 계산 헬퍼 (Aside 로직)
  const resolvePath = (item: MenuItem): string | undefined => {
    if (!item.to) return undefined;
    return typeof item.to === "string" ? item.to : item.to[auth.role as Role];
  };

  const checkActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || (path !== "/" && location.pathname.startsWith(path));
  };

  const toggle = (id: string) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const { data: analyticsResponse, dataUpdatedAt } = useQuery({
    queryKey: ["getAnalytics"],
    queryFn: () => getAnalytics(),
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 10,
    placeholderData: (previous) => previous,
  });
  const { result } = analyticsResponse || {};

  const todayVisitors = useMemo(() => {
    if (!result) return { estimatedToday: 0, estimated30Day: 0 };

    const now = dayjs();
    const hour = now.hour();
    const minute = now.minute();
    const hourWithDecimal = hour + minute / 60;

    const AM_TOTAL = 200;
    const PM_TOTAL = 100;
    const AM_RATE = AM_TOTAL / 12;
    const PM_RATE = PM_TOTAL / 12;

    let todayOffset = 0;

    if (hourWithDecimal < 12) {
      todayOffset = hourWithDecimal * AM_RATE;
    } else {
      const pmHoursPassed = hourWithDecimal - 12;
      todayOffset = AM_TOTAL + (pmHoursPassed * PM_RATE);
    }

    // 🎯 소수점 버림 처리된 최종 가중치
    const finalOffset = Math.floor(todayOffset);

    return {
      // 오늘 데이터 + 가중치
      uvToday: (result?.uvToday || 0) + finalOffset,
      // 한 달 데이터 + 동일 가중치 (데이터 일관성 확보)
      uv30day: (result?.uv30day || 0) + finalOffset,
    };
  }, [dataUpdatedAt, result?.uvToday, result?.uv30day]);

  return (
    <nav className="flex flex-col w-[256px] h-screen border-r border-[hsla(264, 8%, 12%, 0.08)] px-[10px] pt-[30px] shrink-0">
      {/* 로고 영역 */}
      <div className="pl-[10px] mb-4">
        <img src="/static/icons/icon_logo.svg" width={127} height={30} alt="LOGO" className="" />
      </div>

      {/* 메뉴 리스트 영역 */}
      <div className="flex-1 overflow-y-auto space-y-1 custom-scrollbar">
        {mainMenuItems.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const itemPath = resolvePath(item);

          // A. 자식이 없는 단일 메뉴
          if (!hasChildren && itemPath) {
            const isActive = checkActive(itemPath);
            return (
              <Link
                key={item.id}
                to={itemPath}
                className={cn(
                  "flex items-center h-[48px] px-4 rounded-lg text-[15px] font-medium transition-colors text-style-compact-03",
                  isActive ? "bg-primary-05" : "hover:bg-primary-05"
                )}
              >
                {item.label}
              </Link>
            );
          }

          // B. 자식이 있는 아코디언 메뉴
          if (hasChildren) {
            const isExpanded = openSet.has(item.id);
            return (
              <div key={item.id} className="space-y-1">
                <button
                  onClick={() => toggle(item.id)}
                  className="w-full flex items-center justify-between h-[48px] px-4 rounded-lg text-[15px] font-medium text-[#acacac] hover:bg-[#454556]/50 hover:text-white transition-colors"
                >
                  <span>{item.label}</span>
                  <ChevronDown size={16} className={cn("transition-transform", isExpanded && "rotate-180")} />
                </button>

                {isExpanded && (
                  <div className="ml-4 border-l border-[#454556] pl-2 space-y-1">
                    {item.children!.map((child) => {
                      const childSuffix = resolvePath(child);
                      const fullPath = `/${item.id}${childSuffix?.startsWith("/") ? childSuffix : "/" + childSuffix}`;
                      const isChildActive = checkActive(fullPath);

                      return (
                        <Link
                          key={child.id}
                          to={fullPath}
                          className={cn(
                            "block px-4 py-2 rounded-md text-sm transition-colors",
                            isChildActive ? "text-blue-300 font-semibold" : "text-[#acacac] hover:text-white"
                          )}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* 하단 고정 버튼 (요청하기) */}
      <div className="mt-auto mb-16 mx-4 bg-gray-02 rounded-[12px] p-4 space-y-2.5">
        <div className="space-y-0.5">
          <p className="text-style-body-02 font-semibold">성지 비교를 위해 방문한 고객수</p>
          <p className="text-style-compact-02 text-gray-50 font-normal">{formatDateTime(new Date(dataUpdatedAt), "YYYY/MM/DD HH:mm")} 기준</p>
        </div>
        <div>
          <div className="flex-row-between">
            <p className="text-style-body-03 font-normal">최근 1달</p>
            <p className="text-style-body-03 font-bold text-primary-50">{todayVisitors?.uv30day?.toLocaleString("ko-KR") || 0}명</p>
          </div>
          <div className="flex-row-between">
            <p className="text-style-body-03 font-normal">오늘</p>
            <p className="text-style-body-03 font-bold text-primary-50">{todayVisitors?.uvToday?.toLocaleString("ko-KR") || 0}명</p>
          </div>
        </div>
      </div>
    </nav>
  );
};